"use client"

import * as React from "react"
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, startOfYear, endOfMonth, subMonths, subWeeks } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { cn } from "../lib/utils"
import { Button } from "./ui/button"
import { Calendar } from "./ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"

// Preset date ranges
const presets = [
  {
    label: "Today",
    getValue: () => {
      const today = new Date()
      return { from: today, to: today }
    }
  },
  {
    label: "Yesterday",
    getValue: () => {
      const yesterday = subDays(new Date(), 1)
      return { from: yesterday, to: yesterday }
    }
  },
  {
    label: "Last 7 Days",
    getValue: () => ({
      from: subDays(new Date(), 6),
      to: new Date()
    })
  },
  {
    label: "Last 30 Days",
    getValue: () => ({
      from: subDays(new Date(), 29),
      to: new Date()
    })
  },
  {
    label: "Last 90 Days",
    getValue: () => ({
      from: subDays(new Date(), 89),
      to: new Date()
    })
  },
  {
    label: "This Week",
    getValue: () => ({
      from: startOfWeek(new Date(), { weekStartsOn: 1 }),
      to: new Date()
    })
  },
  {
    label: "Last Week",
    getValue: () => {
      const lastWeek = subWeeks(new Date(), 1)
      return {
        from: startOfWeek(lastWeek, { weekStartsOn: 1 }),
        to: endOfWeek(lastWeek, { weekStartsOn: 1 })
      }
    }
  },
  {
    label: "This Month",
    getValue: () => ({
      from: startOfMonth(new Date()),
      to: new Date()
    })
  },
  {
    label: "Last Month",
    getValue: () => {
      const lastMonth = subMonths(new Date(), 1)
      return {
        from: startOfMonth(lastMonth),
        to: endOfMonth(lastMonth)
      }
    }
  },
  {
    label: "This Year",
    getValue: () => ({
      from: startOfYear(new Date()),
      to: new Date()
    })
  }
]

export function DateRangePicker({ dateRange, onDateRangeChange, className }) {
  const [open, setOpen] = React.useState(false)
  // Local state for in-progress selection
  const [localRange, setLocalRange] = React.useState(dateRange)
  
  // Sync local state when prop changes (but not while picker is open)
  React.useEffect(() => {
    if (!open) {
      setLocalRange(dateRange)
    }
  }, [dateRange, open])
  
  // Reset local state when opening the picker
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setLocalRange(dateRange)
    }
    setOpen(isOpen)
  }

  const handlePresetClick = (preset) => {
    const range = preset.getValue()
    onDateRangeChange(range)
    setOpen(false)
  }
  
  const handleApply = () => {
    if (localRange?.from && localRange?.to) {
      onDateRangeChange(localRange)
    }
    setOpen(false)
  }
  
  const handleCancel = () => {
    setLocalRange(dateRange) // Reset to original
    setOpen(false)
  }

  const formatDateRange = () => {
    if (!dateRange?.from) return "Pick a date range"
    if (!dateRange?.to) return format(dateRange.from, "MMM d, yyyy")
    
    // Same day
    if (dateRange.from.toDateString() === dateRange.to.toDateString()) {
      return format(dateRange.from, "MMM d, yyyy")
    }
    
    // Same month and year
    if (
      dateRange.from.getMonth() === dateRange.to.getMonth() &&
      dateRange.from.getFullYear() === dateRange.to.getFullYear()
    ) {
      return `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "d, yyyy")}`
    }
    
    // Same year
    if (dateRange.from.getFullYear() === dateRange.to.getFullYear()) {
      return `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d, yyyy")}`
    }
    
    // Different years
    return `${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}`
  }

  const getDaysSelected = () => {
    if (!localRange?.from || !localRange?.to) return 0
    return Math.ceil((localRange.to - localRange.from) / (1000 * 60 * 60 * 24)) + 1
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal min-w-[220px]",
            !dateRange && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDateRange()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" sideOffset={8}>
        <div className="flex flex-col sm:flex-row">
          {/* Presets sidebar */}
          <div className="border-b sm:border-b-0 sm:border-r bg-muted/30 p-3 sm:w-[150px]">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 px-1">
              Quick Select
            </p>
            <div className="flex flex-wrap sm:flex-col gap-1">
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className="justify-start text-sm h-8 px-2 hover:bg-accent"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Calendar */}
          <div className="p-3">
            <Calendar
              mode="range"
              defaultMonth={localRange?.from || new Date()}
              selected={localRange}
              onSelect={setLocalRange}
              numberOfMonths={2}
              className="rounded-md"
            />
            
            {/* Footer */}
            <div className="flex items-center justify-between pt-3 mt-3 border-t">
              <div className="text-sm text-muted-foreground">
                {localRange?.from && localRange?.to ? (
                  <span>
                    <span className="font-medium text-foreground">{getDaysSelected()}</span>
                    {" day"}{getDaysSelected() !== 1 ? "s" : ""} selected
                  </span>
                ) : localRange?.from ? (
                  <span>Select end date</span>
                ) : (
                  "Select a date range"
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  size="sm" 
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleApply}
                  disabled={!localRange?.from || !localRange?.to}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
